import constants from '../../constants';
import {
  AccountRetention,
  DeidentifyRequest,
  DeidentifyResult,
  RetentionAudit
} from './types';

// A corrupt or partially written session must not throw here. Falling back to
// no session sends an empty bearer token and surfaces an honest 401, which the
// panel already renders as an error state. Mirrors the same guard in
// systemSettings/bulkChargeGuarantee/api.ts.
const getSession = () => {
  const raw = localStorage['session'];
  if (!raw || raw === 'undefined') {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// Read per request rather than once at module load, so a session that changes mid-session (or is
// written after this module is first imported) is picked up.
const getHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    ApiKey: constants.apikey || '',
    'Content-Type': 'application/json',
    Authorization: session?.key ? `bearer ${session.key}` : ''
  };
};

// Every endpoint here answers with the API's { success, data } envelope, so a 200 carrying
// success: false is still a failure and both checks are needed. The message is for the console
// only; each caller renders its own copy from data.json.
const handleApiResponse = async <T>(
  response: Response,
  failureMessage: string
): Promise<T> => {
  const json = await response.json();
  if (!response.ok || !json?.success) {
    throw new Error(failureMessage);
  }
  return json.data as T;
};

// Generic so the audit reads share the transport without casting their way out of a fixed
// AccountRetention return.
const fetchRetention = async <T = AccountRetention>(
  path: string
): Promise<T> => {
  const response = await fetch(
    `${constants.baseApiHost}/api/v2/dataRetentionFlag/${path}`,
    { headers: getHeaders() }
  );
  return handleApiResponse<T>(response, 'retention request failed');
};

const accountPath = (accountId: number) => `account/${accountId}`;

// Keyed on the flag row rather than the email so the address never travels in a URL.
const flagPath = (flagId: number) => `flag/${flagId}/retention`;

// The account detail page prefetches on mount, in parallel with the account record
// request; the panel (which only mounts once that record has rendered) then awaits
// the in-flight promise instead of starting a second, later request. Keyed on the
// path so an account id and a flag id of the same number cannot collide. Entries
// stay for a short TTL (deduping concurrent readers) and expire after it, so an
// unread prefetch can never serve stale data minutes later.
const PREFETCH_TTL_MS = 30_000;
const inflight = new Map<
  string,
  { promise: Promise<AccountRetention>; at: number }
>();

const getOrFetch = (path: string): Promise<AccountRetention> => {
  const cached = inflight.get(path);
  if (cached && Date.now() - cached.at < PREFETCH_TTL_MS) {
    return cached.promise;
  }
  inflight.delete(path);
  return fetchRetention(path);
};

export const prefetchAccountRetention = (accountId: number): void => {
  const path = accountPath(accountId);
  const promise = fetchRetention(path);
  promise.catch(() => {});
  inflight.set(path, { promise, at: Date.now() });
};

export const getAccountRetention = (
  accountId: number
): Promise<AccountRetention> => getOrFetch(accountPath(accountId));

// Re-reads past the prefetch cache and replaces the entry. Needed straight after a de-identify or a
// restore: the cached answer is stale by definition, and the actions menu is driven by this value.
export const refreshAccountRetention = (
  accountId: number
): Promise<AccountRetention> => {
  const path = accountPath(accountId);
  inflight.delete(path);
  const promise = fetchRetention(path);
  promise.catch(() => {});
  inflight.set(path, { promise, at: Date.now() });
  return promise;
};

// Works for any flagged subject, including those with no account of their own
// (guest donors and ticket buyers, leads, event ambassadors).
export const getFlagRetention = (flagId: number): Promise<AccountRetention> =>
  getOrFetch(flagPath(flagId));

// The de-identification calls are v3 POSTs, so subjects travel in the body and no address ever
// appears in a URL or in browser history.
const postDeidentify = async (
  path: string,
  request: DeidentifyRequest
): Promise<DeidentifyResult> => {
  const response = await fetch(
    `${constants.baseApiHost}/api/v3/dataRetentionFlag/${path}`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(request)
    }
  );
  return handleApiResponse<DeidentifyResult>(
    response,
    'de-identification request failed'
  );
};

// Writes nothing. Returns each subject's eligibility plus the rows that would change, counted by the
// same predicates the commit uses, so what the operator confirms is what actually happens.
export const previewDeidentify = (
  request: DeidentifyRequest
): Promise<DeidentifyResult> => postDeidentify('deidentify/preview', request);

export const deidentify = (
  request: DeidentifyRequest
): Promise<DeidentifyResult> => postDeidentify('deidentify', request);

// Only succeeds while the subject's snapshot is inside the retention window.
export const revertDeidentify = (
  request: DeidentifyRequest
): Promise<DeidentifyResult> => postDeidentify('deidentify/revert', request);

// Read straight through fetchRetention: the audit is a plain GET on the same v2 surface, and keying
// on the flag id keeps the address out of the URL exactly as the retention lookups do.
export const getFlagAudit = (flagId: number): Promise<RetentionAudit> =>
  fetchRetention<RetentionAudit>(`flag/${flagId}/audit`);

export const getAccountAudit = (accountId: number): Promise<RetentionAudit> =>
  fetchRetention<RetentionAudit>(`account/${accountId}/audit`);
